import { cn } from "web-design-system";
import {
  FUND_BASKET_STRATEGY_OPTIONS,
  FUND_BASKET_TENURE_OPTIONS,
} from "creso-core/constants";
import { FundBasketDialog } from "@funds/components/baskets/dialog";
import { useFundBaskets } from "creso-core/hooks";
import { useMemo, useState } from "react";
import { BasketDetailsCard } from "@funds/components/baskets/basket-details-card";
import { FundBasketSummary } from "creso-core/types";
import { FundBasketListItem } from "@funds/components/baskets/fund-basket-list-item";
import { AnimatePresence, motion } from "motion/react";
import { ConditionalRenderer } from "@core/components/conditional-renderer";
import { Page } from "@core/components/layout/page";
import { FilterList } from "@core/components/filter-list";

export function FundBasketDashboard() {
  const [filters, setFilters] = useState<Parameters<typeof useFundBaskets>[0]>({
    strategy: [],
    tenure: [],
  });

  const { data: baskets } = useFundBaskets(filters);

  const [selectedBasket, setSelectedBasket] =
    useState<FundBasketSummary | null>(null);

  const allBaskets = useMemo(
    () => Object.values(baskets ?? {}).flat(),
    [baskets]
  );

  const [stuckEdge, setStuckEdge] = useState<"top" | "bottom" | null>(null);
  const [isCollapsed, setIsCollapsed] = useState(false);

  const handleSelectBasket = (basket: FundBasketSummary) => {
    setSelectedBasket(basket);
    setIsCollapsed(true);
  };

  const filtersOptions = useMemo(() => {
    return [
      {
        title: "Risk profile",
        options: FUND_BASKET_STRATEGY_OPTIONS.map((f) => ({
          id: f.value,
          label: f.label,
          count: allBaskets.filter((b) => b.strategy === f.value).length,
          checked: filters?.strategy?.includes(f.value) ?? false,
          onCheckedChange: (id: string) => {
            if (filters?.strategy?.includes(id)) {
              setFilters({
                ...filters,
                strategy: filters?.strategy?.filter((s) => s !== id),
              });
            } else {
              setFilters({
                ...filters,
                strategy: [...(filters?.strategy ?? []), id],
              });
            }
          },
        })),
      },
      {
        title: "Tenure",
        options: FUND_BASKET_TENURE_OPTIONS.map((f) => ({
          id: f.value,
          label: f.label,
          count: allBaskets.filter((b) => b.tenure === f.value).length,
          checked: filters?.tenure?.includes(f.value) ?? false,
          onCheckedChange: (id: string) => {
            if (filters?.tenure?.includes(id)) {
              setFilters({
                ...filters,
                tenure: filters?.tenure?.filter((s) => s !== id),
              });
            } else {
              setFilters({
                ...filters,
                tenure: [...(filters?.tenure ?? []), id],
              });
            }
          },
        })),
      },
    ];
  }, [filters, allBaskets]);

  if (!baskets) return null;

  return (
    <div onClick={() => setSelectedBasket(null)}>
      <Page.Header
        className="top-12 pb-2"
        title="Fund Basket"
        actions={
          <div className="flex flex-row items-center gap-2">
            <FundBasketDialog />
            <div className="lg:hidden">
              <FilterList
                filters={filtersOptions}
                onClearAll={() => setFilters({ strategy: [], tenure: [] })}
              />
            </div>
          </div>
        }
      />
      <Page.Body className="pt-0">
        <div className="flex flex-row gap-6">
          <div className="self-stretch h-[calc(100dvh-10rem)] sticky top-31 hidden lg:block">
            <FilterList
              filters={filtersOptions}
              onClearAll={() => setFilters({ strategy: [], tenure: [] })}
            />
          </div>
          <div className="flex flex-row w-full">
            <div
              className="flex flex-col gap-9 flex-1"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex flex-col">
                <span className="text-lg ml-1 mb-2 font-semibold">
                  Your baskets
                </span>
                {baskets.custom_baskets.length ? (
                  baskets.custom_baskets.map((b, index) => {
                    const isNextItemSelected =
                      allBaskets[index + 1]?.id === selectedBasket?.id;
                    const isLast = index === allBaskets.length - 1;
                    return (
                      <FundBasketListItem
                        key={b.id}
                        basket={b}
                        collapsed={isCollapsed}
                        withDivider={!isNextItemSelected && !isLast}
                        isSelected={selectedBasket?.id === b.id}
                        onSelect={handleSelectBasket}
                        onStick={
                          selectedBasket?.id === b.id
                            ? (v) => setStuckEdge(v)
                            : undefined
                        }
                      />
                    );
                  })
                ) : (
                  <EmptyState />
                )}
                <ConditionalRenderer
                  condition={baskets.curated_baskets.length > 0}
                >
                  <span className="text-lg ml-1 mt-6 mb-2 font-semibold">
                    Curated baskets
                  </span>
                  {baskets.curated_baskets.map((b, i) => {
                    const index = i + baskets.custom_baskets.length;
                    const isNextItemSelected =
                      allBaskets[index + 1]?.id === selectedBasket?.id;
                    const isLast = index === allBaskets.length - 1;
                    return (
                      <FundBasketListItem
                        key={b.id}
                        basket={b}
                        collapsed={isCollapsed}
                        withDivider={!isNextItemSelected && !isLast}
                        isSelected={selectedBasket?.id === b.id}
                        onSelect={handleSelectBasket}
                        onStick={
                          selectedBasket?.id === b.id
                            ? (v) => setStuckEdge(v)
                            : undefined
                        }
                      />
                    );
                  })}
                </ConditionalRenderer>
              </div>
            </div>
            <AnimatePresence onExitComplete={() => setIsCollapsed(false)}>
              {selectedBasket && (
                <motion.div
                  initial={{ opacity: 0, x: 24 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 24 }}
                  transition={{ duration: 0.2, ease: "easeOut" }}
                  onClick={(e) => e.stopPropagation()}
                  className={cn(
                    "w-100 sticky top-31 h-[calc(100dvh-10rem)] border border-border p-6 flex flex-col self-start rounded-lg bg-linear-to-r from-tag-yellow/30 to-background overflow-hidden",
                    stuckEdge === "top" && "rounded-tl-none",
                    stuckEdge === "bottom" && "rounded-bl-none"
                  )}
                >
                  <BasketDetailsCard basket={selectedBasket} />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
        {/* TO hide fund basket list bottom overflow */}
        <div className="absolute bottom-0 left-0 right-0 h-5 z-10 bg-background" />
      </Page.Body>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="border border-border border-dashed h-50 rounded-lg flex flex-col justify-center items-center gap-6">
      <FundBasketDialog variant="outline" />
      <span className="text-sm text-muted-foreground">
        Fund Baskets help you bundle mutual funds into ready-to-use portfolios
      </span>
    </div>
  );
}
